import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Lock, Eye, AlertCircle, Filter, X, ExternalLink, Instagram } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import axios from 'axios';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PublicSharePage = () => {
  const { shareCode } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [requiresPassword, setRequiresPassword] = useState(false);
  const [password, setPassword] = useState('');
  const [activeTagId, setActiveTagId] = useState(searchParams.get('tag') || null);

  const fetchShare = async (pwd = null, tagId = null) => {
    setLoading(true);
    try {
      let url = `${API_URL}/public/share/${shareCode}`;
      const params = new URLSearchParams();
      if (pwd) params.append('password', pwd);
      if (tagId) params.append('filter_tag_id', tagId);
      if (params.toString()) url += `?${params.toString()}`;
      
      const res = await axios.get(url);
      
      if (res.data.requires_password) {
        setRequiresPassword(true);
      } else {
        setData(res.data);
        setRequiresPassword(false);
      }
    } catch (err) {
      if (err.response?.status === 404) {
        setError('Share link not found');
      } else if (err.response?.status === 410) {
        setError('This link has expired');
      } else {
        setError('Failed to load shared content');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchShare(null, activeTagId);
  }, [shareCode]);

  const handleTagFilter = (tagId) => {
    if (tagId === activeTagId) {
      // Clear filter
      setActiveTagId(null);
      setSearchParams({});
      fetchShare(null, null);
    } else {
      setActiveTagId(tagId);
      setSearchParams({ tag: tagId });
      fetchShare(null, tagId);
    }
  };

  const handlePasswordSubmit = (e) => {
    e.preventDefault();
    fetchShare(password, activeTagId);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-violet-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-zinc-950 flex flex-col items-center justify-center p-6">
        <div className="w-16 h-16 rounded-full bg-red-500/10 flex items-center justify-center mb-4">
          <AlertCircle className="w-8 h-8 text-red-400" />
        </div>
        <h1 className="text-xl font-semibold text-white mb-2">{error}</h1>
        <p className="text-zinc-500">The content you're looking for is not available</p>
      </div>
    );
  }

  if (requiresPassword) {
    return (
      <div className="min-h-screen bg-zinc-950 flex flex-col items-center justify-center p-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-md"
        >
          <div className="text-center mb-8">
            <div className="w-16 h-16 rounded-full bg-violet-500/10 flex items-center justify-center mx-auto mb-4">
              <Lock className="w-8 h-8 text-violet-400" />
            </div>
            <h1 className="text-2xl font-bold text-white mb-2">Protected Content</h1>
            <p className="text-zinc-500">Enter password to view</p>
          </div>
          
          <form onSubmit={handlePasswordSubmit} className="space-y-4">
            <Input
              type="password"
              placeholder="Enter password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="bg-zinc-800 border-zinc-700 text-white text-center h-12"
              autoFocus
            />
            <Button
              type="submit"
              className="w-full h-12 bg-violet-600 hover:bg-violet-500"
            >
              View Content
            </Button>
          </form>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-950 p-6 md:p-8">
      {/* Header */}
      <div className="max-w-7xl mx-auto mb-8">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-bold text-white">
            Ad<span className="text-violet-400">Vault</span>
          </h1>
          <div className="flex items-center gap-2 text-sm text-zinc-500">
            <Eye className="w-4 h-4" />
            Shared View
          </div>
        </div>
      </div>

      {/* Tag Filters */}
      {data?.available_tags?.length > 0 && (
        <div className="max-w-7xl mx-auto mb-6">
          <div className="flex items-center gap-3 flex-wrap">
            <div className="flex items-center gap-2 text-sm text-zinc-500">
              <Filter className="w-4 h-4" />
              <span>Filter by tag:</span>
            </div>
            {data.available_tags.map((tag) => (
              <Badge
                key={tag.id}
                onClick={() => handleTagFilter(tag.id)}
                className={`
                  cursor-pointer transition-all
                  ${activeTagId === tag.id 
                    ? 'bg-violet-600 text-white border-violet-500 ring-2 ring-violet-400/50' 
                    : 'bg-zinc-800/80 text-zinc-300 border-zinc-700 hover:bg-zinc-700'
                  }
                `}
                data-testid={`filter-tag-${tag.id}`}
              >
                <span
                  className="w-2 h-2 rounded-full mr-1.5"
                  style={{ backgroundColor: tag.color }}
                />
                {tag.name}
                {activeTagId === tag.id && (
                  <X className="w-3 h-3 ml-1" />
                )}
              </Badge>
            ))}
            {activeTagId && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => handleTagFilter(activeTagId)}
                className="text-zinc-400 hover:text-white h-7 px-2"
              >
                Clear filter
              </Button>
            )}
          </div>
        </div>
      )}

      {/* Content Grid */}
      <div className="max-w-7xl mx-auto">
        {data?.ads?.length === 0 ? (
          <div className="text-center py-20">
            <p className="text-zinc-500">
              {activeTagId ? 'No ads match this filter' : 'No ads in this share'}
            </p>
            {activeTagId && (
              <Button
                variant="ghost"
                onClick={() => handleTagFilter(activeTagId)}
                className="mt-4 text-violet-400 hover:text-violet-300"
              >
                Clear filter
              </Button>
            )}
          </div>
        ) : (
          <div className="masonry-grid">
            {data?.ads?.map((ad, index) => (
              <motion.div
                key={ad.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
                className="masonry-item group"
              >
                <div className="bg-zinc-900/50 rounded-xl overflow-hidden border border-white/5">
                  {ad.thumbnail_url && (
                    <div className="relative">
                      <img
                        src={ad.thumbnail_url.startsWith('/api') 
                          ? `${process.env.REACT_APP_BACKEND_URL}${ad.thumbnail_url}` 
                          : ad.thumbnail_url
                        }
                        alt={ad.title || 'Ad'}
                        className="w-full h-auto"
                        loading="lazy"
                      />
                      {/* Source URL Link - appears on hover */}
                      {ad.source_url && (
                        <a
                          href={ad.source_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          className="absolute top-3 right-3 p-2 rounded-full bg-black/60 text-white hover:bg-black/80 transition-all opacity-0 group-hover:opacity-100 hover:opacity-100"
                          title="View original post"
                        >
                          <ExternalLink className="w-4 h-4" />
                        </a>
                      )}
                    </div>
                  )}
                  <div className="p-4">
                    {ad.title && (
                      <h3 className="font-medium text-zinc-200 mb-2">{ad.title}</h3>
                    )}
                    {ad.ai_description && (
                      <p className="text-sm text-zinc-400 line-clamp-3">{ad.ai_description}</p>
                    )}
                    
                    {/* Source URL Link */}
                    {ad.source_url && (
                      <a
                        href={ad.source_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1.5 mt-3 text-sm text-violet-400 hover:text-violet-300 transition-colors"
                      >
                        {ad.source_url.includes('instagram.com') ? (
                          <Instagram className="w-4 h-4" />
                        ) : (
                          <ExternalLink className="w-4 h-4" />
                        )}
                        <span className="truncate max-w-[200px]">View original</span>
                      </a>
                    )}
                    
                    {/* Show assigned tags */}
                    {ad.tag_ids?.length > 0 && data.available_tags && (
                      <div className="flex flex-wrap gap-1.5 mt-3">
                        {ad.tag_ids.map((tagId) => {
                          const tag = data.available_tags.find(t => t.id === tagId);
                          if (!tag) return null;
                          return (
                            <span
                              key={tagId}
                              onClick={() => handleTagFilter(tagId)}
                              className="px-2 py-0.5 text-xs rounded-full bg-violet-500/20 text-violet-300 border border-violet-500/30 cursor-pointer hover:bg-violet-500/30 flex items-center gap-1"
                            >
                              <span
                                className="w-1.5 h-1.5 rounded-full"
                                style={{ backgroundColor: tag.color }}
                              />
                              {tag.name}
                            </span>
                          );
                        })}
                      </div>
                    )}
                    {/* Show AI tags if no assigned tags */}
                    {(!ad.tag_ids || ad.tag_ids.length === 0) && ad.ai_tags?.length > 0 && (
                      <div className="flex flex-wrap gap-1.5 mt-3">
                        {ad.ai_tags.slice(0, 4).map((tag, i) => (
                          <span
                            key={i}
                            className="px-2 py-0.5 text-xs rounded-full bg-zinc-800 text-zinc-400"
                          >
                            {tag}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default PublicSharePage;
